type Recovery =
  | "reload-requested"
  | "already-requested"
  | "offline"
  | "storage-unavailable"
  | "reload-failed";

type Options = {
  buildId: string;
  target: EventTarget;
  getStorage: () => Pick<Storage, "getItem" | "setItem">;
  isOnline: () => boolean;
  reload: () => void;
  report: (error: unknown, recovery: Recovery) => void;
};

// FS-2026-036: retain the rejected import until navigation completes. Cancelling
// vite:preloadError makes Vite 4 resolve it as undefined, which breaks React.lazy.
export function installPreloadRecovery({
  buildId,
  target,
  getStorage,
  isOnline,
  reload,
  report,
}: Options) {
  const key = `foggosport_preload_reload:${buildId}`;
  let requested = false;

  const handleError = (event: Event) => {
    const error =
      (event as Event & { payload?: unknown }).payload ??
      new Error("Vite dynamic import preload failed");
    let recovery: Recovery = "already-requested";
    if (!isOnline()) {
      recovery = "offline";
    } else if (!requested) {
      try {
        const storage = getStorage();
        if (!storage.getItem(key)) {
          storage.setItem(key, "1");
          requested = true;
          recovery = "reload-requested";
        }
      } catch {
        // Without persistent tab state an automatic reload could loop.
        recovery = "storage-unavailable";
      }
    }

    report(error, recovery);
    if (recovery === "reload-requested") {
      try {
        reload();
      } catch {
        report(error, "reload-failed");
      }
    }
  };

  target.addEventListener("vite:preloadError", handleError);
  return () => target.removeEventListener("vite:preloadError", handleError);
}
